/**
 * RESPONSIBILITY: what a section looks like before anyone has ever saved it.
 *
 * This is what makes an EMPTY environment work. `main` ships with no seed at all, so
 * every section renders these values on its first request — not an error, not a
 * blank page, just an unfilled site waiting for an editor.
 */
import { rowFields, type FieldMeta, type SectionMeta } from '../decorators';
import { EMPTY_IMAGE } from '../index';

function defaultFor(f: FieldMeta): unknown {
  if (f.default !== undefined) return f.default;
  switch (f.kind) {
    case 'num': return f.min ?? 0;
    case 'bool': return false;
    case 'list': return [];
    case 'stringlist': return [];
    case 'img':
    case 'video': return { ...EMPTY_IMAGE };
    // With `in` there is nothing to pick from until the vocabulary exists, so empty is
    // the only honest default — the first fixed option would be a value nobody chose.
    case 'choice': return f.multiple ? [] : (f.in ? '' : f.options?.[0] ?? '');
    default: return '';
  }
}

export const defaultData = (section: SectionMeta): Record<string, unknown> =>
  Object.fromEntries(section.fields.map((f) => [f.key, defaultFor(f)]));

type Doc = Record<string, unknown>;

/**
 * A STORED document, completed with the defaults for whatever it does not carry.
 *
 * Adding a field to a section that has already been saved used to produce a page with a
 * hole in it: the stored JSON has no such key, so the view rendered nothing, and the only
 * repair was a migration per environment per field. Nothing is overwritten — a key that
 * is present, including one deliberately saved empty, is left exactly as it is. Only
 * genuinely ABSENT keys are filled.
 *
 * Recursive into list rows, because a column added to a row type is the same problem one
 * level down and the rows are where most fields live.
 */
export function withDefaults(section: SectionMeta, doc: Doc): Doc {
  return fillInto(section.fields, doc);
}

function fillInto(fields: FieldMeta[], doc: Doc): Doc {
  const out: Doc = { ...doc };

  for (const f of fields) {
    if (out[f.key] === undefined) out[f.key] = defaultFor(f);

    if (f.kind === 'list' && f.of && Array.isArray(out[f.key])) {
      const columns = rowFields(f.of());
      out[f.key] = (out[f.key] as unknown[]).map((row) =>
        row && typeof row === 'object' && !Array.isArray(row)
          ? fillInto(columns, row as Doc)
          : row);
    }
  }

  return out;
}
